import { AuthRepository } from '../data/auth-repository';
import { LoginRequest, TokenInfo } from '../types';
import { checkIsDistributor, getTokenExpiry } from '../auth-utils';

export interface LoginResult {
  success: boolean;
  tokenInfo: TokenInfo | null;
  isDistributor: boolean;
  error?: string;
}

/**
 * LoginUseCase - Business Logic for User Login
 */
export class LoginUseCase {
  constructor(private readonly authRepository: AuthRepository) {}

  async execute(credentials: LoginRequest): Promise<LoginResult> {
    try {
      // Validate input - check for empty or whitespace-only values
      if (!credentials.username?.trim() || !credentials.password?.trim()) {
        return {
          success: false,
          tokenInfo: null,
          isDistributor: false,
          error: 'Email and password are required'
        };
      }

      // Validate password minimum length
      if (credentials.password.trim().length < 4) {
        return {
          success: false,
          tokenInfo: null,
          isDistributor: false,
          error: 'Password must be at least 4 characters'
        };
      }

      // Call API to login
      const response = await this.authRepository.loginWithCredentials(credentials);

      // Save tokens (operatorId and distributorId can be undefined for distributor users)
      await this.authRepository.saveTokens(
        response.token,
        response.token,
        response.accountId,
        response.operatorId,
        response.distributorId
      );

      // Build token info
      const tokenInfo: TokenInfo = {
        appToken: response.token,
        accountToken: response.token,
        accountId: response.accountId,
        operatorId: response.operatorId,
        distributorId: response.distributorId,
        expiry: getTokenExpiry(response.token)
      };

      // Check if user is distributor
      const isDistributor = checkIsDistributor(tokenInfo);

      return {
        success: true,
        tokenInfo,
        isDistributor
      };
    } catch (error) {
      return {
        success: false,
        tokenInfo: null,
        isDistributor: false,
        error: error instanceof Error ? error.message : 'Login failed. Please try again.'
      };
    }
  }
}
